import { useRef, useState, useEffect } from "react";
import api, { apiError, API, openPdf } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PenLine, Check, Download, Eraser } from "lucide-react";

export function SignContract({
  onSigned,
  basePath = "/agent/contract",
  docPdfUrl,
  title = "Signer mon contrat de travail",
  subtitle = "Lisez le contrat, signez au doigt, puis validez avec le code SMS.",
  signedTitle = "Contrat de travail signé",
  signedFilename = "mon-contrat.pdf",
  readLabel = "Lire le contrat (PDF)",
}) {
  const pdfUrl = docPdfUrl || `${API}/documents/contrat-travail.pdf`;
  const [contract, setContract] = useState(null);
  const [step, setStep] = useState("draw");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [sending, setSending] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const load = () => api.get(`${basePath}/mine`).then((r) => setContract(r.data.contract)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const getPos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = getPos(e);
    ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#1A1A1A"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke();
    hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const requestOtp = async () => {
    if (!hasDrawn.current) return toast.error("Veuillez signer d'abord");
    setSending(true);
    try {
      const { data } = await api.post(`${basePath}/request-otp`);
      setDemoOtp(data.demo_otp || "");
      setStep("otp");
      toast.success(data.demo_otp ? `Code (démo) : ${data.demo_otp}` : "Code SMS envoyé à votre numéro");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const doSign = async () => {
    if (otp.length < 4) return toast.error("Entrez le code reçu");
    setSending(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      await api.post(`${basePath}/sign`, { signature, otp });
      toast.success("Document signé ✅");
      setStep("draw"); setOtp(""); load(); onSigned && onSigned();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  if (contract?.status === "signed") {
    return (
      <div data-testid="sign-contract-card" className="bg-white rounded-2xl border border-[#16a34a]/40 p-6 lg:col-span-2">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Check className="w-5 h-5 text-[#16a34a]" /> {signedTitle}</h2>
        <p className="text-sm text-[#75807A] mt-1">Signé le {new Date(contract.signed_at).toLocaleString("fr-FR")}. Document verrouillé et archivé.</p>
        <button data-testid="sign-download" onClick={async () => { try { await openPdf(`${basePath}/${contract.id}/pdf`, signedFilename); } catch { toast.error("Impossible d'ouvrir le document"); } }} className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1A1A1A] text-white font-bold"><Download className="w-4 h-4" /> Télécharger le document</button>
      </div>
    );
  }

  return (
    <div data-testid="sign-contract-card" className="bg-white rounded-2xl border-2 border-[#D94F2B]/40 p-6 lg:col-span-2">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><PenLine className="w-5 h-5 text-[#D94F2B]" /> {title}</h2>
      <p className="text-sm text-[#75807A] mt-1">{subtitle}</p>
      <a href={pdfUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#D94F2B]"><Download className="w-4 h-4" /> {readLabel}</a>

      <div className="mt-4">
        <div className="rounded-xl border-2 border-dashed border-[#E2DFD8] bg-[#FDF8F0] overflow-hidden">
          <canvas ref={canvasRef} data-testid="sign-canvas" width={600} height={200}
            className="w-full touch-none" style={{ height: 200, cursor: "crosshair" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <button data-testid="sign-clear" onClick={clear} className="mt-2 inline-flex items-center gap-1 text-xs text-[#75807A] hover:text-[#D32F2F]"><Eraser className="w-3.5 h-3.5" /> Effacer</button>
      </div>

      {step !== "otp" ? (
        <Button data-testid="sign-request-otp" onClick={requestOtp} disabled={sending} className="mt-3 h-11 px-5 rounded-xl bg-[#F26419] text-white font-bold">{sending ? "..." : "Recevoir le code"}</Button>
      ) : (
        <div className="mt-3 space-y-3">
          {demoOtp && (
            <div data-testid="sign-demo-otp" className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-[#75807A] uppercase tracking-wide">Code (démo)</div>
                <div className="font-head font-black text-xl tracking-[0.3em] text-[#1A1A1A]">{demoOtp}</div>
              </div>
              <button data-testid="sign-use-demo-otp" onClick={() => setOtp(demoOtp)} className="text-sm font-semibold text-[#D94F2B] underline">Utiliser ce code</button>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="sign-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Code reçu" className="h-11 rounded-xl border-2 w-48" />
          <Button data-testid="sign-confirm" onClick={doSign} disabled={sending} className="h-11 px-5 rounded-xl bg-[#16a34a] text-white font-bold">{sending ? "..." : "Signer"}</Button>
          <button data-testid="sign-resend" onClick={requestOtp} className="text-xs text-[#D94F2B] font-semibold">Renvoyer le code</button>
          </div>
        </div>
      )}
    </div>
  );
}
